import React, { useState, useEffect, useRef, useCallback } from "react";
import "./SSRCheck.css";
import { FaTwitter, FaLinkedin, FaTelegram, FaGoogle } from "react-icons/fa";

const SSRCheck = () => {
  const [url, setUrl] = useState("");
  const [metaData, setMetaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useProxy, setUseProxy] = useState(false);
  const autoTriggerRef = useRef(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Read URL from query parameters on component mount
  useEffect(() => {
    const search = window.location.search;

    // Extract URL manually to handle & characters in the URL value
    if (search.includes("url=")) {
      const urlParam = search.split("url=")[1];

      if (urlParam) {
        // Decode the URL parameter
        const decodedUrl = decodeURIComponent(urlParam);
        setUrl(decodedUrl);
        autoTriggerRef.current = true;
      }
    }
  }, []);

  const handleCheckSSR = useCallback(async () => {
    if (!url) {
      setError("Please enter a URL");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let html;
      let baseUrl;

      // Check if URL is localhost
      const isLocalhost =
        url.includes("localhost") || url.includes("127.0.0.1");

      // Use proxy if checkbox is checked, or for non-localhost URLs
      if (isLocalhost && !useProxy) {
        try {
          // Fetch directly for localhost URLs
          const response = await fetch(url, {
            mode: "cors",
            credentials: "same-origin",
          });
          if (!response.ok) {
            throw new Error(
              `HTTP error! status: ${response.status} ${response.statusText}`
            );
          }
          html = await response.text();
          baseUrl = new URL(url).origin;
        } catch (localErr) {
          console.error("Localhost fetch error:", localErr);

          // Provide more specific error messages
          let errorMessage = "Failed to fetch localhost URL. ";

          // CORS error (most common for localhost)
          if (
            localErr.message.includes("Failed to fetch") ||
            localErr.name === "TypeError"
          ) {
            errorMessage = `❌ CORS Error: The server at ${url} is blocking this request.\n\n`;
            errorMessage += `To fix this, add CORS headers to your server:\n`;
            errorMessage += `• Access-Control-Allow-Origin: *\n`;
            errorMessage += `• Access-Control-Allow-Methods: GET, OPTIONS\n\n`;
            errorMessage += `Or use the proxy fallback (trying now...)`;

            setError(errorMessage);

            // Try using CORS proxy as fallback
            setTimeout(async () => {
              try {
                setError("Retrying with CORS proxy...");
                const response = await fetch(
                  `https://api.allorigins.win/get?url=${encodeURIComponent(
                    url
                  )}`
                );
                const data = await response.json();
                const html = data.contents;
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");

                const getMetaContent = (property, name) => {
                  const metaTag =
                    doc.querySelector(`meta[property="${property}"]`) ||
                    doc.querySelector(`meta[name="${name}"]`);
                  return metaTag ? metaTag.getAttribute("content") : null;
                };

                let imageUrl =
                  getMetaContent("og:image", "twitter:image") || "/logo192.png";
                const urlOrigin = new URL(url).origin;
                if (
                  imageUrl &&
                  !imageUrl.startsWith("http") &&
                  !imageUrl.startsWith("data:")
                ) {
                  if (imageUrl.startsWith("/")) {
                    imageUrl = urlOrigin + imageUrl;
                  } else {
                    imageUrl = urlOrigin + "/" + imageUrl;
                  }
                }

                const metadata = {
                  title:
                    getMetaContent("og:title", "twitter:title") ||
                    doc.querySelector("title")?.textContent ||
                    "No title found",
                  description:
                    getMetaContent("og:description", "description") ||
                    "No description available",
                  image: imageUrl,
                  url: getMetaContent("og:url", "canonical") || url,
                  siteName:
                    getMetaContent("og:site_name", "application-name") ||
                    new URL(url).hostname,
                };

                setMetaData(metadata);
                setError(null);
              } catch (proxyErr) {
                console.error("Proxy fallback failed:", proxyErr);
                setError(
                  `❌ Both direct fetch and proxy failed.\n\nDirect: CORS blocked\nProxy: ${proxyErr.message}`
                );
              } finally {
                setLoading(false);
              }
            }, 1000);

            return;
          } else if (localErr.message.includes("404")) {
            errorMessage += `Page not found (404) at ${url}`;
          } else if (localErr.message.includes("500")) {
            errorMessage += `Server error (500) at ${url}`;
          } else {
            errorMessage += localErr.message;
          }

          setError(errorMessage);
          setLoading(false);
          return;
        }
      } else {
        // Use CORS proxy for external URLs
        const response = await fetch(
          `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`
        );
        const data = await response.json();
        html = data.contents;
        baseUrl = new URL(url).origin;
      }

      // Parse meta tags
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const getMetaContent = (property, name) => {
        const metaTag =
          doc.querySelector(`meta[property="${property}"]`) ||
          doc.querySelector(`meta[name="${name}"]`);
        return metaTag ? metaTag.getAttribute("content") : null;
      };

      // Get image and resolve relative URLs
      let imageUrl =
        getMetaContent("og:image", "twitter:image") || "/logo192.png";
      if (
        imageUrl &&
        !imageUrl.startsWith("http") &&
        !imageUrl.startsWith("data:")
      ) {
        // Resolve relative URLs
        if (imageUrl.startsWith("/")) {
          imageUrl = baseUrl + imageUrl;
        } else {
          imageUrl = baseUrl + "/" + imageUrl;
        }
      }

      const metadata = {
        title:
          getMetaContent("og:title", "twitter:title") ||
          doc.querySelector("title")?.textContent ||
          "No title found",
        description:
          getMetaContent("og:description", "description") ||
          "No description available",
        image: imageUrl,
        url: getMetaContent("og:url", "canonical") || url,
        siteName:
          getMetaContent("og:site_name", "application-name") ||
          new URL(url).hostname,
      };

      setMetaData(metadata);
    } catch (err) {
      setError("Failed to fetch URL metadata. Check the console for details.");
      console.error("Error details:", err);
    } finally {
      setLoading(false);
    }
  }, [url, useProxy]);

  // Auto-trigger check when URL is set from query params
  useEffect(() => {
    if (autoTriggerRef.current && url) {
      autoTriggerRef.current = false;
      const timer = setTimeout(() => {
        handleCheckSSR();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [url, handleCheckSSR]);

  const generateShareUrl = () => {
    const params = new URLSearchParams();

    if (url) {
      params.set("url", url);
    }

    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?${params.toString()}`;
  };

  const handleShare = async () => {
    const shareUrl = generateShareUrl();

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="ssr-check-container">
      <div className="ssr-check-content">
        <h1 className="ssr-title">
          <span className="ssr-icon">🔍</span>
          SSR Preview Checker
        </h1>
        <p className="ssr-subtitle">
          Check how your URL appears on social media platforms
        </p>

        <div className="ssr-input-section">
          <input
            type="text"
            className="ssr-input"
            placeholder="Enter URL (e.g., https://example.com)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleCheckSSR()}
          />
          <button
            className="ssr-button"
            onClick={handleCheckSSR}
            disabled={loading}
          >
            {loading ? "Checking..." : "Check SSR"}
          </button>
          <button
            className="ssr-share-button"
            onClick={handleShare}
            title="Share Configuration"
            disabled={!url}
          >
            {copyFeedback ? "✓" : "🔗"}
          </button>
        </div>

        <div className="ssr-options">
          <label className="ssr-checkbox-label">
            <input
              type="checkbox"
              checked={useProxy}
              onChange={(e) => setUseProxy(e.target.checked)}
              className="ssr-checkbox"
            />
            <span>Use proxy for localhost (bypass CORS)</span>
          </label>
        </div>

        {error && <div className="ssr-error">{error}</div>}

        {metaData && (
          <div className="ssr-previews">
            {/* Twitter Preview */}
            <div className="preview-card">
              <div className="preview-header">
                <FaTwitter className="platform-icon twitter" />
                <h3>Twitter Card</h3>
              </div>
              <div className="twitter-preview">
                <div className="twitter-card">
                  {metaData.image && (
                    <div className="twitter-image-container">
                      <img
                        src={metaData.image}
                        alt="Preview"
                        onError={(e) => {
                          e.target.src = "/logo192.png";
                        }}
                      />
                    </div>
                  )}
                  <div className="twitter-content">
                    <div className="twitter-title">{metaData.title}</div>
                    <div className="twitter-description">
                      {metaData.description}
                    </div>
                    <div className="twitter-url">
                      <FaLink /> {new URL(metaData.url).hostname}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* LinkedIn Preview */}
            <div className="preview-card">
              <div className="preview-header">
                <FaLinkedin className="platform-icon linkedin" />
                <h3>LinkedIn Card</h3>
              </div>
              <div className="linkedin-preview">
                <div className="linkedin-card">
                  {metaData.image && (
                    <div className="linkedin-image-container">
                      <img
                        src={metaData.image}
                        alt="Preview"
                        onError={(e) => {
                          e.target.src = "/logo192.png";
                        }}
                      />
                    </div>
                  )}
                  <div className="linkedin-content">
                    <div className="linkedin-title">{metaData.title}</div>
                    <div className="linkedin-url">
                      {new URL(metaData.url).hostname}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Telegram Preview */}
            <div className="preview-card">
              <div className="preview-header">
                <FaTelegram className="platform-icon telegram" />
                <h3>Telegram Card</h3>
              </div>
              <div className="telegram-preview">
                <div className="telegram-card">
                  {metaData.image && (
                    <div className="telegram-image-container">
                      <img
                        src={metaData.image}
                        alt="Preview"
                        onError={(e) => {
                          e.target.src = "/logo192.png";
                        }}
                      />
                    </div>
                  )}
                  <div className="telegram-content">
                    <div className="telegram-site">{metaData.siteName}</div>
                    <div className="telegram-title">{metaData.title}</div>
                    <div className="telegram-description">
                      {metaData.description}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Search Preview */}
            <div className="preview-card">
              <div className="preview-header">
                <FaGoogle className="platform-icon google" />
                <h3>Google Search Result</h3>
              </div>
              <div className="google-preview">
                <div className="google-card">
                  <div className="google-url">
                    <span className="google-icon">🌐</span>
                    {metaData.url}
                  </div>
                  <div className="google-title">{metaData.title}</div>
                  <div className="google-description">
                    {metaData.description}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!metaData && !loading && !error && (
          <div className="ssr-placeholder">
            <div className="placeholder-content">
              <div className="placeholder-icon">🚀</div>
              <h3>Enter a URL to see preview</h3>
              <p>
                Check how your website appears on Twitter, LinkedIn, Telegram,
                and Google Search
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FaLink = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="currentColor"
    style={{ marginRight: "4px" }}
  >
    <path d="M6.354 5.5H4a3 3 0 0 0 0 6h3a3 3 0 0 0 2.83-4H9c-.086 0-.17.01-.25.031A2 2 0 0 1 7 10.5H4a2 2 0 1 1 0-4h1.535c.218-.376.495-.714.82-1z" />
    <path d="M9 5.5a3 3 0 0 0-2.83 4h1.098A2 2 0 0 1 9 6.5h3a2 2 0 1 1 0 4h-1.535a4.02 4.02 0 0 1-.82 1H12a3 3 0 1 0 0-6H9z" />
  </svg>
);

export default SSRCheck;
